import { createDeserializer } from "@jsonapi-serde/client";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { dtMetaSchema } from "@/components/Forms/schema/dtMetaSchema.ts";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";

const pendingCaseSchema = z.object({
    idUUID: z.string(),
    caseStatus: z.string().optional().nullable(),
    checkReceived: z.boolean().optional().nullable(),
    umCheckReceived: z.boolean().optional().nullable(),
});

// CAM-563: the badge components only count tasks and read the related case's status fields,
// so no task attributes are parsed here. The paired cambre-api change sparse-fields them out
// of the response as well; until it lands they are sent and simply ignored.
const pendingCaseTaskSchema = z.object({});

export const resourceSelector = createDeserializer({
    type: "caseTask",
    cardinality: "many",
    attributesSchema: pendingCaseTaskSchema,
    documentMetaSchema: dtMetaSchema,
    relationships: {
        case: {
            type: "case",
            cardinality: "one",
            included: {
                attributesSchema: pendingCaseSchema,
            },
        },
    },
});

export const caseTasksSelector = (input: unknown) => resourceSelector(input).data;
export type CaseTasksPendingDocument = ReturnType<typeof caseTasksSelector>;
export type CaseTaskPending = CaseTasksPendingDocument[number];

export const useFetchCaseTasksPending = (): UseQueryResult<CaseTasksPendingDocument> => {
    const fetch = useAuthenticatedFetch();

    return useQuery({
        queryKey: ["case-tasks-pending"],
        queryFn: async () => {
            const response = await fetch(apiUrl("/case-task/pending"), {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
            });

            return await response.json();
        },
        select: caseTasksSelector,
        // CAM-563: paging the cases grid remounts the badge components; without a staleTime the
        // query refetched the full pending-task document on every page change. Task mutations
        // invalidate this key, so a remount inside the window still shows fresh counts.
        staleTime: 60_000,
    });
};
